import { useEffect, useState } from "react";
import firebase from "firebase/app";
import "firebase/firestore";
import { FirebaseWebConfig } from "../firebase/appFirebase";

/**
 * The slice of an app config this hook needs. For logged-in pages this is the
 * Redux `appConfig` (where `webConfig` is a JSON string); for the public
 * /activity pages it is `{ webConfig }` built from the unauthenticated
 * `live/apps/{appKey}/firebase` endpoint (where `webConfig` is already an object).
 */
export interface FirebaseProjectConfig {
	webConfig?: string | FirebaseWebConfig | null;
}

const getApiKey = (app: firebase.app.App): string | undefined =>
	(app.options as { apiKey?: string }).apiKey;

/**
 * Initialize (or reuse) a named Firebase app for a white-label app's project so
 * pages can act on that project's Auth/Firestore without touching the control
 * panel's default Firebase app. Instances are named `<appKey>-firebase<n>`;
 * the app key is used (never the display name) so the name has no spaces.
 * Returns `null` until the config is available.
 */
const useFirebaseProject = (
	appConfig?: FirebaseProjectConfig | null,
	appKey?: string
): firebase.app.App | null => {
	const [firebaseApp, setFirebaseApp] = useState<firebase.app.App | null>(null);

	useEffect(() => {
		if (!appConfig || !appConfig.webConfig || !appKey) return;

		const webConfig: FirebaseWebConfig =
			typeof appConfig.webConfig === "string" ? JSON.parse(appConfig.webConfig) : appConfig.webConfig;

		// If an existing firebase instance has the same API key, reuse it.
		let projectFirebase = firebase.apps.find(a => getApiKey(a) === webConfig.apiKey);

		if (!projectFirebase) {
			// Otherwise auto-increment the instance name for this app so we never hit
			// an "app already initialized" error.
			const allFirebaseNames = firebase.apps.map(a => a.name).filter(a => a !== "[DEFAULT]");
			if (allFirebaseNames.length === 0) {
				projectFirebase = firebase.initializeApp(webConfig, `${appKey}-firebase1`);
			} else {
				const newestInstance = allFirebaseNames
					.filter(a => a.includes(appKey))
					.sort()
					.splice(-1)
					.pop();
				const newestId = newestInstance ? parseInt(newestInstance.replace(/(\S+)-firebase(\d+)/, "$2")) : 1;
				const newId = newestId + 1;
				projectFirebase = firebase.initializeApp(webConfig, `${appKey}-firebase${newId}`);
			}
		}
		setFirebaseApp(projectFirebase);
	}, [appConfig, appKey]);

	return firebaseApp;
};

export default useFirebaseProject;
